import React, { useEffect, useState } from 'react';
import ConfigPanelBody from '../ConfigPanelBody';
import ConfigPanelFooter from '../ConfigPanelFooter';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import { useOperator } from './OperatorProvider';
import flowSource from '../../../../sources/flow';
import graphSource from '../../../../sources/graph';
import FormGroup from '../../../generic/FormGroup';
import { getByID } from '../../../../utils/core';
import Toggle from '../../../Toggle';

const NavigationOverrideDetails = () => {
    const {
        flowId,
        editingToken,
        mapElement,
        setCurrentScreen,
        selectedNavigationIndex,
        setSelectedNavigationIndex,
        OPERATOR_SCREENS,
        tenantId,
        authenticationToken,
        notifyError,
    } = useOperator();

    // State values
    const [navigations, setNavigations] = useState([]);
    const [selectedNavigationId, setSelectedNavigationId] = useState(
        mapElement.navigationOverrides?.[selectedNavigationIndex]?.navigationElementId ?? null,
    );
    const [mapElements, setMapElements] = useState([]);
    const [selectedItemId, setSelectedItemId] = useState(
        mapElement.navigationOverrides?.[selectedNavigationIndex]?.navigationItemId ?? null,
    );
    const [navigationItems, setNavigationItems] = useState([]);
    const [selectedMapElementId, setSelectedMapElementId] = useState(
        mapElement.navigationOverrides?.[selectedNavigationIndex]?.locationMapElementId ?? null,
    );
    const [isEnabled, setIsEnabled] = useState(
        mapElement.navigationOverrides?.[selectedNavigationIndex]?.isEnabled ?? true,
    );
    const [isVisible, setIsVisible] = useState(
        mapElement.navigationOverrides?.[selectedNavigationIndex]?.isVisible ?? true,
    );
    const [hasSubmitted, setHasSubmitted] = useState(false);

    const isNavigationValid = selectedNavigationId !== null;
    const isItemValid = selectedItemId !== null;

    const isFormValid = isNavigationValid && isItemValid;

    const onSave = () => {
        setHasSubmitted(true);
        if (isFormValid) {
            const index = selectedNavigationIndex ?? mapElement.navigationOverrides?.length ?? 0;
            const selectedNavigationElement = getByID(selectedNavigationId, navigations);
            const selectedMapElement = getByID(selectedMapElementId, mapElements);
            const selectedItem = getByID(selectedItemId, navigationItems);

            const navigationOverride = {
                isEnabled,
                isVisible,
                locationMapElementDeveloperName: selectedMapElement?.developerName ?? null,
                locationMapElementId: selectedMapElementId,
                navigationElementDeveloperName: selectedNavigationElement.developerName,
                navigationElementId: selectedNavigationId,
                navigationItemDeveloperName: selectedItem.developerName,
                navigationItemId: selectedItemId,
            };

            if (!mapElement.navigationOverrides) {
                mapElement.navigationOverrides = [];
            }

            mapElement.navigationOverrides[index] = navigationOverride;

            setCurrentScreen(OPERATOR_SCREENS.configuration);
        }
    };

    const onCancel = () => {
        setCurrentScreen(OPERATOR_SCREENS.configuration);
        setSelectedNavigationIndex(null);
    };

    const fetchNavigations = () => {
        flowSource
            .getNavigationElements({
                tenantId,
                authenticationToken,
                flowId,
                editingToken,
            })
            .then((response) => {
                setNavigations(response);
            })
            .catch(notifyError);
    };

    const fetchNavigationItems = (navigationElementId) => {
        flowSource
            .getNavigationElements({
                tenantId,
                authenticationToken,
                flowId,
                editingToken,
                navigationElementId,
            })
            .then((response) => {
                setNavigationItems(response.navigationItems);
            })
            .catch(notifyError);
    };

    const fetchMapElements = () => {
        graphSource
            .getMapElements(flowId, editingToken, tenantId, authenticationToken)
            .then((response) => {
                setMapElements(response);
            })
            .catch(notifyError);
    };

    const onChangeNavigation = ({ target: { value } }) => {
        fetchNavigationItems(value);
        setSelectedNavigationId(value);
    };

    useEffect(() => {
        fetchNavigations();

        if (selectedNavigationId !== null) {
            fetchNavigationItems(selectedNavigationId);
        }

        fetchMapElements();
    }, []);

    return (
        <>
            <ConfigPanelBody>
                <h3 className="margin-top-small">{`Navigation Override for: ${mapElement.developerName}`}</h3>

                <FormGroup
                    label="Which Navigation do you want to override?"
                    isRequired
                    isValid={isNavigationValid}
                    validationMessage="Please select a Navigation."
                    showValidation={hasSubmitted}
                >
                    <select
                        className="form-control form-select form-control-width"
                        value={selectedNavigationId ?? ''}
                        onChange={onChangeNavigation}
                        data-testid="navigation-select"
                    >
                        {selectedNavigationId === null && (
                            <option value="" key="empty">
                                Select a Navigation
                            </option>
                        )}
                        {navigations.map((navigation) => (
                            <option value={navigation.id} key={navigation.id}>
                                {navigation.developerName}
                            </option>
                        ))}
                    </select>
                </FormGroup>

                <FormGroup
                    label="Which item do you want to override?"
                    isRequired
                    isValid={isItemValid}
                    validationMessage="Please select a Navigation item."
                    showValidation={hasSubmitted}
                >
                    <select
                        className="form-control form-select form-control-width"
                        value={selectedItemId ?? ''}
                        onChange={({ target: { value } }) => setSelectedItemId(value)}
                        disabled={selectedNavigationId === null}
                        data-testid="navigation-item-select"
                    >
                        {selectedItemId === null && selectedNavigationId !== null && (
                            <option value="" key="empty">
                                Select an item
                            </option>
                        )}
                        {navigationItems.map((navigation) => (
                            <option value={navigation.id} key={navigation.id}>
                                {navigation.developerName}
                            </option>
                        ))}
                    </select>
                </FormGroup>

                <FormGroup label="Change the location of the item to:">
                    <select
                        className="form-control form-select form-control-width"
                        value={selectedMapElementId ?? ''}
                        onChange={({ target: { value } }) => setSelectedMapElementId(value)}
                        data-testid="navigation-location-select"
                    >
                        {selectedMapElementId === null && (
                            <option value="" key="empty">
                                Select a Map Element
                            </option>
                        )}
                        {mapElements.map((mapElementLocation) => (
                            <option value={mapElementLocation.id} key={mapElementLocation.id}>
                                {mapElementLocation.developerName}
                            </option>
                        ))}
                    </select>
                </FormGroup>

                <FormGroup>
                    <label>
                        <Toggle isOn={isVisible} onChange={() => setIsVisible(!isVisible)} />
                        Should the item be visible to users?
                    </label>
                </FormGroup>

                <FormGroup>
                    <label>
                        <Toggle isOn={isEnabled} onChange={() => setIsEnabled(!isEnabled)} />
                        Should the item be enabled for users to select?
                    </label>
                </FormGroup>
            </ConfigPanelBody>
            <ConfigPanelFooter>
                <ButtonDefault className="flex-child-right" onClick={onCancel}>
                    Cancel
                </ButtonDefault>
                <ButtonPrimary className="margin-left" onClick={onSave}>
                    {selectedNavigationIndex === null ? 'Add' : 'Update'}
                </ButtonPrimary>
            </ConfigPanelFooter>
        </>
    );
};

export default NavigationOverrideDetails;
